import { useEffect, useRef, useState } from 'react'
import MatchEvents from './MatchEvents'
import { getFixtureDetail, type FixtureDetail } from '../api/football'
import './FixtureDetailPanel.css'

/**
 * The expandable body of a fixtures-page row: the match timeline, and the
 * per-team stats once they exist.
 *
 * It lives on the Fixtures page rather than the predict grid because that is where a fixture stays
 * reachable — browse back to gameweek 3 in December and its timeline is still there, served from
 * the same stored rows. The row owns the disclosure and `aria-expanded`, this owns the fetching. It
 * is mounted only while expanded, so "fetch on first expand" is simply mount, and collapse cancels
 * by unmounting.
 *
 * While expanded **and** in play it refetches whenever `liveVersion` changes: the live poll every
 * client already runs is the heartbeat, so the panel rides it instead of adding a timer of its own.
 * The request carries the previous ETag, so a timeline that hasn't moved costs a 304.
 */

interface FixtureDetailPanelProps {
  fixtureId: string
  /** Which side is which — the stats rows arrive keyed by team id, in no particular order. */
  homeTeamId: string
  awayTeamId: string
  /** Labelled by the card's disclosure control. */
  id?: string
  /**
   * A value that changes whenever the live poll reports movement on this fixture (its score,
   * status or minute). Left undefined for a settled fixture, whose timeline cannot change.
   */
  liveVersion?: string
}

function FixtureDetailPanel({
  fixtureId,
  homeTeamId,
  awayTeamId,
  id,
  liveVersion,
}: FixtureDetailPanelProps) {
  const [detail, setDetail] = useState<FixtureDetail | null>(null)
  const [failed, setFailed] = useState(false)
  const etag = useRef<string | null>(null)

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const response = await getFixtureDetail(fixtureId, etag.current)
        if (cancelled) return
        etag.current = response.etag
        // `null` is a 304 — the timeline is unchanged, so leaving state alone is both correct and
        // the reason this can ride a 10-second poll.
        if (response.detail) setDetail(response.detail)
        setFailed(false)
      } catch {
        // A fixture with no detail (or a server that briefly can't answer) is a quiet empty
        // state, never a broken card.
        if (!cancelled) setFailed(true)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [fixtureId, liveVersion])

  if (failed && detail === null) {
    return (
      <div className="fixture-detail" id={id}>
        <p className="fixture-detail__note">Match details aren't available.</p>
      </div>
    )
  }

  if (detail === null) {
    return (
      <div className="fixture-detail" id={id}>
        <p className="fixture-detail__note">Loading…</p>
      </div>
    )
  }

  const home = detail.teams.find((team) => team.teamId === homeTeamId)
  const away = detail.teams.find((team) => team.teamId === awayTeamId)

  return (
    <div className="fixture-detail" id={id}>
      <MatchEvents events={detail.timeline} partial={detail.partial} />
      {/* Per-team statistics stay empty until the post-match pass, so an absent block renders as
          nothing at all — never as a row of zeroes, which would read as a real measurement. */}
      {home && away && (
        <div className="fixture-detail__stats">
          {STAT_ROWS.map((row) => {
            const homeValue = row.read(home)
            const awayValue = row.read(away)
            if (homeValue === null && awayValue === null) return null
            return (
              <div key={row.label} className="fixture-detail__stat">
                <span className="fixture-detail__stat-value">{homeValue ?? '–'}</span>
                <span className="fixture-detail__stat-label">{row.label}</span>
                <span className="fixture-detail__stat-value">{awayValue ?? '–'}</span>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

const STAT_ROWS: { label: string; read: (team: FixtureDetail['teams'][number]) => number | null }[] = [
  { label: 'Possession', read: (team) => team.possessionPercent },
  { label: 'Shots', read: (team) => team.shotsTotal },
  { label: 'Corners', read: (team) => team.corners },
]

export default FixtureDetailPanel
